import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { playTrack, setTrack } from "./lib/audio";
import Nav from "./components/Nav";
import FloatingPlayer from "./components/FloatingPlayer";
import Hero from "./components/Hero";
import HubOrbit from "./components/HubOrbit";
import BrandPortals from "./components/BrandPortals";
import BrandMap from "./components/BrandMap";
import AboutNelle from "./components/AboutNelle";
import MusicArchive from "./components/MusicArchive";
import BeautyStyle from "./components/BeautyStyle";
import CultureIdeas from "./components/CultureIdeas";
import Poetry from "./components/Poetry";
import HubEditBar from "./components/HubEditor";
import { HubContentProvider } from "./lib/hubContent";
import Faith from "./components/Faith";
import SiteFooter from "./components/SiteFooter";
import PrismaticPage from "./components/pe/PrismaticPage";
import PEAuthPage, { PEAuthCallback } from "./components/pe/PEAuthPage";
import PEAccountPage from "./components/pe/PEAccountPage";
import PEAdminPage from "./components/pe/PEAdminPage";
import PEConfirmationPage from "./components/pe/PEConfirmationPage";
import PEGuestPage from "./components/pe/PEGuestPage";
import { PEAuthProvider } from "./components/pe/PEAuthContext";
import "@/App.css";

const ColorBand = () => (
  <div aria-hidden="true" className="h-2 md:h-2.5 bg-gradient-to-r from-en-blue via-en-pink to-en-orange" />
);

function HubHome() {
  return (
    <HubContentProvider>
      <Nav />
      <main id="main-content">
        <Hero />
        <ColorBand />
        <HubOrbit />
        <ColorBand />
        <BrandPortals />
        <BrandMap />
        <ColorBand />
        <AboutNelle />
        <ColorBand />
        <MusicArchive />
        <ColorBand />
        <BeautyStyle />
        <CultureIdeas />
        <Poetry />
        <Faith />
      </main>
      <SiteFooter />
      <HubEditBar />
    </HubContentProvider>
  );
}

const PAGE_TITLES = {
  "/": "Eclectically Nelle — The Hub of It All",
  "/prismatic-essence": "Prismatic Essence — Beauty in Every Dimension",
  "/prismatic-essence/auth": "Sign in — Prismatic Essence",
  "/prismatic-essence/account": "Your dashboard — Prismatic Essence",
  "/prismatic-essence/admin": "House back office — Prismatic Essence",
  "/prismatic-essence/confirmation": "Booking confirmation — Prismatic Essence",
  "/prismatic-essence/guest": "Guest appointment access — Prismatic Essence",
};

function RouteTrack() {
  const { pathname } = useLocation();
  useEffect(() => {
    setTrack(pathname.startsWith("/prismatic-essence") ? "essence" : "hub");
    document.title = PAGE_TITLES[pathname] || PAGE_TITLES["/"];
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <PEAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HubHome />} />
      <Route path="/prismatic-essence" element={<PrismaticPage />} />
      <Route path="/prismatic-essence/auth" element={<PEAuthPage />} />
      <Route path="/prismatic-essence/account" element={<PEAccountPage />} />
      <Route path="/prismatic-essence/admin" element={<PEAdminPage />} />
      <Route path="/prismatic-essence/confirmation" element={<PEConfirmationPage />} />
      <Route path="/prismatic-essence/guest" element={<PEGuestPage />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    const start = () => {
      if (!sessionStorage.getItem("en_music_off")) playTrack();
    };
    // Music only ever starts from the explicit player toggle (FloatingPlayer),
    // never from an arbitrary first click — unexpected audio interferes with
    // screen readers (WCAG 1.4.2). The toggle persists its state.
    return undefined;
  }, []);

  return (
    <BrowserRouter>
      <PEAuthProvider>
        <div data-testid="app-root" className="bg-en-cream text-en-ink font-sans antialiased overflow-x-clip">
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <div className="grain-overlay" aria-hidden="true" />
          <RouteTrack />
          <AppRoutes />
          <FloatingPlayer />
          <Toaster position="bottom-center" />
        </div>
      </PEAuthProvider>
    </BrowserRouter>
  );
}
